import { Episode } from '@/types';
import { isEpisodeFree } from './isEpisodeFree';
import { shouldShowSignInModalForEpisode, shouldShowSubscriptionModalForEpisode } from './freeEpisodeThreshold';

export interface EpisodeAccessContext {
  episodeId: string;
  seriesId?: string;
  seasonId?: string;
  episodeIndex: number;
  isFree: boolean;
  episodes: Episode[];
}

export type AccessDenyReason = 'not_authenticated' | 'not_subscribed' | null;

export interface EpisodeAccessResult {
  accessGranted: boolean;
  requiresAuth: boolean;
  requiresSubscription: boolean;
  denyReason: AccessDenyReason;
}

export function checkEpisodeAccess(
  context: EpisodeAccessContext,
  hasUser: boolean,
  isSubscribed: boolean
): EpisodeAccessResult {
  const { episodeIndex, episodes, isFree } = context;
  const episode = episodes[episodeIndex];

  console.log('[DEEPLINK][PRECHECK] Checking access:', {
    episodeId: context.episodeId,
    episodeIndex,
    isFree,
    hasUser,
    isSubscribed,
    totalEpisodes: episodes.length,
  });

  if (isFree) {
    const requiresAuthForFreeEpisode = shouldShowSignInModalForEpisode(episodes, episodeIndex, hasUser);

    if (requiresAuthForFreeEpisode) {
      console.log('[DEEPLINK][PRECHECK] Free episode but requires auth (beyond threshold)');
      return {
        accessGranted: false,
        requiresAuth: true,
        requiresSubscription: false,
        denyReason: 'not_authenticated',
      };
    }

    console.log('[DEEPLINK][PRECHECK] Free episode - access granted');
    return {
      accessGranted: true,
      requiresAuth: false,
      requiresSubscription: false,
      denyReason: null,
    };
  }

  if (!hasUser) {
    console.log('[DEEPLINK][PRECHECK] Premium episode - requires authentication');
    return {
      accessGranted: false,
      requiresAuth: true,
      requiresSubscription: false,
      denyReason: 'not_authenticated',
    };
  }

  if (!isSubscribed && episode) {
    const requiresSubscription = shouldShowSubscriptionModalForEpisode(episode, hasUser, isSubscribed);

    if (requiresSubscription) {
      console.log('[DEEPLINK][PRECHECK] Premium episode - requires subscription');
      return {
        accessGranted: false,
        requiresAuth: false,
        requiresSubscription: true,
        denyReason: 'not_subscribed',
      };
    }
  }

  console.log('[DEEPLINK][PRECHECK] Access granted - user is subscribed or has access');
  return {
    accessGranted: true,
    requiresAuth: false,
    requiresSubscription: false,
    denyReason: null,
  };
}

export function buildEpisodeAccessContext(
  episodes: Episode[],
  targetEpisodeId: string,
  seriesId?: string,
  seasonId?: string
): EpisodeAccessContext | null {
  const episodeIndex = episodes.findIndex(ep => ep.id === targetEpisodeId);

  if (episodeIndex === -1) {
    console.log('[DEEPLINK][PRECHECK] Episode not found in list:', targetEpisodeId);
    return null;
  }

  const episode = episodes[episodeIndex];
  const isFree = isEpisodeFree(episode.is_free);

  return {
    episodeId: targetEpisodeId,
    seriesId,
    seasonId,
    episodeIndex,
    isFree,
    episodes,
  };
}
